import React, { useContext } from 'react';
import classNames from 'classnames';

import { LayoutContext } from 'lib/ContextTypes';
import { isLeadPackageAdjacent } from 'lib/packageHelper';
import RelatedContent from 'components/RelatedContent';
import { PymFrame } from 'components/Pym/Frame';
import { EmbedInfo } from './EmbedInfo';

import styles from './styles.module.scss';

/**
 * Extracts the embed URL from the given content object.
 * @param {import('../../../lib/graphqlTypeDefs.mjs').Package} content - The content object containing items.
 * @returns {string|boolean} The embed URL if available, otherwise false.
 */
const getEmbedUrl = (content) => content?.items?.[0]?.computedValues?.url ?? false;

/**
 * Retrieves the content type from the given content object.
 * @param {import('../../../lib/graphqlTypeDefs.mjs').Package} content - The content object containing items.
 * @returns {string|boolean} The type of the first item if available, otherwise false.
 */
const getContentType = (content) => content?.items?.[0]?.type ?? false;

/**
 * Extracts toolkit style classes from a ramen embed URL.
 * Looks for a segment containing 'ramen-classes_' in the URL, then parses and formats the style classes.
 * Replaces '~' with spaces in the style classes.
 * @param {string} url - The embed URL to parse.
 * @returns {string} The extracted style classes with 'no-margin', or an empty string if not found.
 */
const getToolkitClassesFromEmbedURL = (url) => {
  if (typeof url !== 'string') {
    return '';
  }
  let ramenClasses;
  let styleClasses;
  if (url.includes('/ramen-classes_')) {
    const params = url.split('/');
    ramenClasses = params.filter((param) => param.includes('ramen-classes'));
    const [first = ''] = ramenClasses;
    [, styleClasses = ''] = first.split('_');
    if (styleClasses.includes('~')) {
      styleClasses = styleClasses.replace(/~/g, ' ');
    }
    return `${styleClasses} no-margin`;
  }
  return '';
};

/**
 * Embed component for rendering custom embedded content within a package.
 * Handles layout, styling, and related content display based on context and content metadata.
 *
 * @param {object} props
 * @param {import('../../../lib/graphqlTypeDefs.mjs').Package} props.content - The content object containing embed data and metadata.
 * @returns {JSX.Element|null} The rendered embed package section, or null if not applicable.
 */
export const Embed = ({ content }) => {
  const { layoutIndex, leadPackageType, isRailAdjacent } = useContext(LayoutContext);

  const embedUrl = getEmbedUrl(content);
  const contentType = getContentType(content);

  if (contentType !== 'custom' || !embedUrl) {
    return null;
  }

  const {
    id,
    name,
    packageIndex,
    items: [
      embedItem,
    ],
    context: {
      railContext,
    } = {},
    subType,
    metadata: {
      headline,
      headlineUrl,
      dek,
      showBreakingHeader = false,
      label,
      showLabel,
      showBottomBorder = false,
    },
  } = content || {};

  const { related = [] } = embedItem;

  const toolKitClasses = getToolkitClassesFromEmbedURL(embedUrl);
  const hasCustomClasses = !!toolKitClasses;
  const leadPkgAdj = layoutIndex === 0
    && packageIndex === 1
    && isLeadPackageAdjacent(leadPackageType);
  const isLeadPkg = layoutIndex === 0 && packageIndex === 0;
  const isStacked = subType === 'stacked';

  const showTopBorder = !isLeadPkg && !leadPkgAdj && headline && !isStacked;
  const embedClass = classNames(
    'pkg',
    styles.embedPackage,
    {
      [styles.topBorder]: showTopBorder,
      [styles.leadPackageAdjacent]: leadPkgAdj,
      [styles.customClasses]: hasCustomClasses,
      [styles.stacked]: isStacked,
      [toolKitClasses]: hasCustomClasses,
    },
  );

  return (
    <>
      <section
        className={embedClass}
        data-packageid={id}
        data-test="embed-pkg"
        data-testid="embed-pkg"
      >
        <EmbedInfo
          headline={headline}
          headlineUrl={headlineUrl}
          dek={dek}
          isBreaking={showBreakingHeader}
          label={label}
          showLabel={showLabel}
          isNotRail={!railContext}
          isStacked={isStacked}
        />
        <div>
          <PymFrame url={embedUrl} title={name} />
        </div>
        {!!related.length && (
          <div
            className={styles.related}
            data-test="embed-pkg__related-content"
            data-testid="embed-pkg__related-content"
          >
            <RelatedContent isRailAdjacent={isRailAdjacent} related={related} />
          </div>
        )}
      </section>
      {showBottomBorder && <div className={styles.embedBottomBorder}><hr /></div>}
    </>
  );
};
